import React, { useMemo, useState } from 'react';
import { Card, Row, Col, Typography, message, Drawer, Form, Input, Select, Space } from 'antd';
import { PlusOutlined, SettingOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchSqlData, insertData, updateData } from '../../../services/api';
import { useTranslation } from 'react-i18next';
import StandardTable, { createActionColumn } from '../../../components/common/StandardTable';
import FilterBar from '../../../components/common/FilterBar';
import type { AdmVehicle } from '../../../types';
import Button from '../../../components/common/Button';

const { Title } = Typography;

interface MaintType {
  id: number;
  name: string;
  description?: string | null;
  applicable_to_machine_types?: string[] | null;
}

const MachMaintenanceTypes: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: rows = [], isLoading } = useQuery<MaintType[], Error>({
    queryKey: ['adm_maintenance_types'],
    queryFn: () => fetchSqlData('adm_maintenance_types'),
    staleTime: 10 * 60_000,
  });
  const { data: vehicles = [] } = useQuery<AdmVehicle[], Error>({
    queryKey: ['adm_vehicles_for_types'],
    queryFn: () => fetchSqlData('adm_vehicles'),
    staleTime: 5 * 60_000,
  });

  const machineTypeOptions = useMemo(() => {
    const s = new Set<string>();
    (vehicles || []).forEach(v => { const t = (v.machine_type || '').trim(); if (t) s.add(t); });
    return Array.from(s).sort().map(t => ({ value: t, label: t }));
  }, [vehicles]);

  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    if (!search) return rows;
    const s = search.toLowerCase();
    return rows.filter(r =>
      (r.name || '').toLowerCase().includes(s) ||
      (r.description || '').toLowerCase().includes(s) ||
      (Array.isArray(r.applicable_to_machine_types) ? r.applicable_to_machine_types.join(' ').toLowerCase().includes(s) : false)
    );
  }, [rows, search]);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<MaintType | null>(null);
  const [form] = Form.useForm();

  const columns = [
    { title: t('machinery.maintenance.types.name'), dataIndex: 'name', key: 'name', sorter: (a: MaintType, b: MaintType) => (a.name || '').localeCompare(b.name || '') },
    { title: t('machinery.maintenance.common.description'), dataIndex: 'description', key: 'description' },
    { title: t('machinery.maintenance.types.applicableTypes'), dataIndex: 'applicable_to_machine_types', key: 'applicable_to_machine_types', render: (arr?: string[]) => Array.isArray(arr) ? arr.join(', ') : '-' },
  ];

  const handleAdd = () => {
    setEditing(null);
    form.resetFields();
    setOpen(true);
  };

  const handleEdit = (record: MaintType) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      description: record.description || undefined,
      applicable_to_machine_types: record.applicable_to_machine_types || [],
    });
    setOpen(true);
  };
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const normalizedName = (values.name || '').trim();
      const existingWithSameName = rows.find(r =>
        r.id !== editing?.id &&
        (r.name || '').trim().toLocaleLowerCase('tr-TR') === normalizedName.toLocaleLowerCase('tr-TR')
      );

      if (existingWithSameName) {
        message.error(t('machinery.maintenance.types.duplicateName'));
        return;
      }

      const payload: Partial<MaintType> = {
        name: normalizedName,
        description: values.description?.trim() || null,
        applicable_to_machine_types: Array.isArray(values.applicable_to_machine_types) ? values.applicable_to_machine_types : null,
      };
      if (editing?.id) {
        await updateData('adm_maintenance_types', editing.id, payload);
        message.success(t('machinery.maintenance.types.updateSuccess'));
      } else {
        await insertData('adm_maintenance_types', payload);
        message.success(t('machinery.maintenance.types.addSuccess'));
      }
      setOpen(false);
      setEditing(null);
      queryClient.invalidateQueries({ queryKey: ['adm_maintenance_types'] });
    } catch (e: any) {
      message.error(e?.message || t('machinery.maintenance.common.operationFailed'));
    }
  };

  return (
    <Card>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            <SettingOutlined style={{ marginRight: 8 }} />
            {t('machinery.maintenance.types.title')}
          </Title>
        </Col>
        <Col>
          <Button action="add" type="primary" icon={<PlusOutlined />} onClick={handleAdd}>{t('machinery.maintenance.types.addButton')}</Button>
        </Col>
      </Row>

      <FilterBar
        items={[
          { type: 'text', key: 'search', placeholder: t('machinery.maintenance.common.searchNameDescription'), value: search, onChange: v => setSearch(v), width: 300 },
        ]}
        onReset={() => setSearch('')}
      />

      <StandardTable
        rowKey="id"
        columns={[
          ...columns,
          createActionColumn<MaintType>({
            onEdit: handleEdit,
            showView: false,
            showDelete: false,
            align: 'left',
          }) as any,
        ] as any}
        dataSource={filtered}
        loading={isLoading}
        bordered
        size="small"
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />

      <Drawer title={editing ? t('machinery.maintenance.types.editTitle') : t('machinery.maintenance.types.addTitle')} open={open} onClose={() => setOpen(false)} width={520} destroyOnClose>
        <Form layout="vertical" form={form}>
          <Form.Item name="name" label={t('machinery.maintenance.types.name')} rules={[{ required: true, message: t('machinery.maintenance.types.nameRequired') }]}>
            <Input placeholder={t('machinery.maintenance.types.namePlaceholder')} />
          </Form.Item>
          <Form.Item name="description" label={t('machinery.maintenance.common.description')}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="applicable_to_machine_types" label={t('machinery.maintenance.types.applicableTypesLabel')}>
            <Select
              mode="multiple"
              allowClear
              options={machineTypeOptions}
              placeholder={t('machinery.maintenance.types.applicableTypesPlaceholder')}
            />
          </Form.Item>
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => setOpen(false)}>{t('machinery.machines.common.cancel')}</Button>
            <Button action={editing ? 'edit' : 'add'} type="primary" onClick={handleSubmit}>{t('machinery.machines.common.save')}</Button>
          </Space>
        </Form>
      </Drawer>
    </Card>
  );
};

export default MachMaintenanceTypes;

